import { isPlatformBrowser, Location } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DefaultUrlSerializer, UrlHandlingStrategy, UrlTree } from '@angular/router';

import { LanguageService } from '@offconon/shared/utils/services/language';

@Injectable({
  providedIn: 'root',
})
export class DomainUrlHandlingStrategy implements UrlHandlingStrategy {
  private readonly platformId = inject<Object>(PLATFORM_ID);
  private readonly location = inject(Location);
  private readonly serializer = new DefaultUrlSerializer();
  private readonly languageService = inject(LanguageService);

  private useLanguageCodeInUrl = true;
  private supportedLanguages: Set<string> | null = null;
  private lastDetectedLanguage: string | null = null;

  shouldProcessUrl(): boolean {
    return true;
  }

  configure(options: { useLanguageCodeInUrl?: boolean; supportedLanguages?: string[] }) {
    this.useLanguageCodeInUrl = options.useLanguageCodeInUrl ?? this.useLanguageCodeInUrl;
    if (Array.isArray(options.supportedLanguages) && options.supportedLanguages.length > 0) {
      const normalized = options.supportedLanguages
        .map((lang) => lang.trim().toLowerCase())
        .filter((lang) => lang.length > 0);
      this.supportedLanguages = new Set(normalized);
    } else {
      this.supportedLanguages = null;
    }
  }

  extract(url: UrlTree): UrlTree {
    const serialized = this.serializer.serialize(url);
    if (!this.useLanguageCodeInUrl) {
      this.applyDefaultLanguage();
      return url;
    }

    const pathWithoutQuery = this.stripQueryParams(serialized);
    const normalizedPath = this.ensureLeadingSlash(pathWithoutQuery);
    const { detectedLanguage, remainingSegments } = this.detectLanguageFromSegments(normalizedPath);

    if (!detectedLanguage) {
      return url;
    }

    this.languageService.setLanguage(detectedLanguage);
    this.lastDetectedLanguage = detectedLanguage;

    const mergedSegments = [
      detectedLanguage,
      ...this.removeDuplicatedLanguageSegment(detectedLanguage, remainingSegments),
    ];
    const finalPath = this.ensureLeadingSlash('/' + mergedSegments.filter(Boolean).join('/'));

    const parsed = this.serializer.parse(finalPath);
    parsed.queryParams = { ...parsed.queryParams, ...url.queryParams };
    return parsed;
  }

  merge(newUrlPart: UrlTree): UrlTree {
    return newUrlPart;
  }

  initializeLanguage(defaultLanguage: string) {
    const rawStored = this.languageService.getLanguage();
    const storedNormalized = rawStored ? rawStored.trim().toLowerCase() : '';
    const storedSupported = storedNormalized
      ? this.resolveSupportedLanguage(storedNormalized)
      : null;
    const fallbackLanguage =
      (defaultLanguage && defaultLanguage.trim().toLowerCase()) ||
      this.languageService.getDefaultLanguage();

    const effectiveStored = storedSupported || fallbackLanguage;
    const isBrowser = isPlatformBrowser(this.platformId);

    if (!this.useLanguageCodeInUrl) {
      const rawPath = this.stripQueryParams(this.location.path());
      const normalizedPath = this.ensureLeadingSlash(rawPath);
      const { detectedLanguage, remainingSegments } =
        this.detectLanguageFromSegments(normalizedPath);

      const effectiveLanguage = detectedLanguage || effectiveStored;
      this.languageService.setLanguage(effectiveLanguage);
      this.lastDetectedLanguage = effectiveLanguage;

      if (isBrowser && detectedLanguage) {
        const pathWithoutLang =
          remainingSegments.length > 0 ? `/${remainingSegments.join('/')}` : '/';
        if (pathWithoutLang !== normalizedPath) {
          this.location.go(pathWithoutLang);
        }
      }
      return;
    }

    const rawPath = this.stripQueryParams(this.location.path());
    const normalizedPath = this.ensureLeadingSlash(rawPath);
    const { detectedLanguage, remainingSegments } = this.detectLanguageFromSegments(normalizedPath);

    const effectiveLanguage = detectedLanguage || storedSupported || fallbackLanguage;
    this.languageService.setLanguage(effectiveLanguage);
    this.lastDetectedLanguage = effectiveLanguage;

    const cleanedSegments = this.removeDuplicatedLanguageSegment(
      effectiveLanguage,
      remainingSegments,
    );
    const finalPath =
      cleanedSegments.length > 0
        ? `/${[effectiveLanguage, ...cleanedSegments].join('/')}`
        : `/${effectiveLanguage}`;

    if (isBrowser && finalPath !== normalizedPath) {
      this.location.go(finalPath);
    }
  }

  normalizePathForRequest(path: string): string {
    const normalizedPath = this.ensureLeadingSlash(this.stripQueryParams(path || '/'));
    if (normalizedPath === '/') {
      return '/';
    }

    const segments = normalizedPath.split('/').filter((segment) => segment.length > 0);
    if (segments.length === 0) {
      return '/';
    }

    const firstSegment = segments[0];
    const detectedLanguage = this.resolveSupportedLanguage(firstSegment);

    // Only strip language code when useLanguageCodeInUrl is false
    // When useLanguageCodeInUrl is true, keep the language code for the API
    const shouldStripLanguage = detectedLanguage !== null && !this.useLanguageCodeInUrl;

    if (!shouldStripLanguage) {
      return normalizedPath;
    }

    const remaining = segments.slice(1);
    return remaining.length > 0 ? `/${remaining.join('/')}` : '/';
  }

  private applyDefaultLanguage() {
    const rawStored = this.languageService.getLanguage();
    const storedNormalized = rawStored ? rawStored.trim().toLowerCase() : '';
    const effectiveLanguage = storedNormalized || this.languageService.getDefaultLanguage();

    this.languageService.setLanguage(effectiveLanguage);
    this.lastDetectedLanguage = effectiveLanguage;
  }

  private stripQueryParams(url: string): string {
    const questionIndex = url.indexOf('?');
    return questionIndex === -1 ? url : url.substring(0, questionIndex);
  }

  private ensureLeadingSlash(value: string): string {
    if (!value) {
      return '/';
    }
    return value.startsWith('/') ? value : `/${value}`;
  }

  private detectLanguageFromSegments(path: string): {
    detectedLanguage: string | null;
    remainingSegments: string[];
  } {
    const segments = path.split('/').filter((segment) => segment.length > 0);
    if (segments.length === 0) {
      return { detectedLanguage: null, remainingSegments: [] };
    }
    const first = segments[0];
    const detectedLanguage = this.resolveSupportedLanguage(first);

    const remainingSegments = detectedLanguage ? segments.slice(1) : segments;
    return { detectedLanguage, remainingSegments };
  }

  private resolveSupportedLanguage(candidate?: string): string | null {
    if (!candidate) {
      return null;
    }
    const normalized = candidate.toLowerCase();

    if (this.supportedLanguages && this.supportedLanguages.size > 0) {
      return this.supportedLanguages.has(normalized) ? normalized : null;
    }

    return this.languageService.isSupportedLanguage(normalized) ? normalized : null;
  }

  private removeDuplicatedLanguageSegment(language: string, segments: string[]): string[] {
    if (segments.length === 0) {
      return segments;
    }

    const [first, ...rest] = segments;
    const normalized = this.resolveSupportedLanguage(first);
    if (normalized === language) {
      return rest;
    }
    return segments;
  }
}
